import * as XLSX from 'xlsx';

export interface ParsedContact {
  email: string;
  name: string;
  status: 'valid' | 'duplicate' | 'invalid';
  errorMessage?: string;
  originalLine?: string;
}

export interface ImportStats {
  total: number;
  valid: number;
  duplicates: number;
  invalid: number;
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function cleanEmail(email: string): string {
  return email
    .toLowerCase()
    .trim()
    .replace(/["'<>]/g, '');
}

export function detectDelimiter(text: string): ',' | ';' | '\t' | null {
  const firstLine = text.split('\n')[0];

  if (firstLine.includes('\t')) return '\t';
  if (firstLine.includes(';')) return ';';
  if (firstLine.includes(',')) return ',';

  return null;
}

export function extractEmailFromText(text: string): string | null {
  const emailRegex = /[^\s@]+@[^\s@]+\.[^\s@]+/;
  const match = text.match(emailRegex);
  return match ? cleanEmail(match[0]) : null;
}

export function isHeaderLine(line: string, delimiter: string | null): boolean {
  const lowerLine = line.toLowerCase();
  const headerKeywords = ['email', 'e-mail', 'naam', 'name', 'voornaam', 'achternaam', 'adres'];

  if (delimiter) {
    const parts = line.split(delimiter);
    return parts.some(part =>
      headerKeywords.some(keyword => part.toLowerCase().trim().includes(keyword))
    );
  }

  return headerKeywords.some(keyword => lowerLine.includes(keyword));
}

export function parseContactLine(
  line: string,
  delimiter: string | null,
  existingEmails: Set<string>,
  importedEmails: Set<string>
): ParsedContact | null {
  if (!line.trim()) return null;

  let email = '';
  let name = '';

  if (delimiter) {
    const parts = line.split(delimiter).map(p => p.trim().replace(/["']/g, ''));

    const emailIndex = parts.findIndex(p => p.includes('@'));

    if (emailIndex !== -1) {
      email = cleanEmail(parts[emailIndex]);
      const otherParts = parts.filter((_, idx) => idx !== emailIndex);
      name = otherParts.join(' ').trim();
    } else {
      return null;
    }
  } else {
    const extractedEmail = extractEmailFromText(line);
    if (!extractedEmail) return null;

    email = extractedEmail;
    name = line.replace(email, '').trim().replace(/[<>()]/g, '').trim();
  }

  if (!isValidEmail(email)) {
    return {
      email,
      name,
      status: 'invalid',
      errorMessage: 'Ongeldig email formaat',
      originalLine: line,
    };
  }

  if (existingEmails.has(email)) {
    return {
      email,
      name,
      status: 'duplicate',
      errorMessage: 'Email bestaat al in je lijst',
      originalLine: line,
    };
  }

  if (importedEmails.has(email)) {
    return {
      email,
      name,
      status: 'duplicate',
      errorMessage: 'Duplicaat in deze import',
      originalLine: line,
    };
  }

  importedEmails.add(email);

  return {
    email,
    name,
    status: 'valid',
    originalLine: line,
  };
}

export function parseImportText(
  text: string,
  existingEmails: string[]
): { contacts: ParsedContact[]; delimiter: string | null } {
  const existingEmailSet = new Set(existingEmails.map(e => cleanEmail(e)));
  const importedEmails = new Set<string>();
  const contacts: ParsedContact[] = [];

  const lines = text.split('\n').filter(line => line.trim());
  if (lines.length === 0) return { contacts: [], delimiter: null };

  const delimiter = detectDelimiter(text);

  const isFirstLineHeader = lines.length > 0 && isHeaderLine(lines[0], delimiter);
  const startIndex = isFirstLineHeader ? 1 : 0;

  for (let i = startIndex; i < lines.length; i++) {
    const parsed = parseContactLine(lines[i], delimiter, existingEmailSet, importedEmails);
    if (parsed) {
      contacts.push(parsed);
    }
  }

  return { contacts, delimiter };
}

export function calculateStats(contacts: ParsedContact[]): ImportStats {
  return {
    total: contacts.length,
    valid: contacts.filter(c => c.status === 'valid').length,
    duplicates: contacts.filter(c => c.status === 'duplicate').length,
    invalid: contacts.filter(c => c.status === 'invalid').length,
  };
}

export function generateExampleCSV(): string {
  return `email,naam
jan@example.com,Jan
piet@bedrijf.nl,Piet
marie@email.com,Marie`;
}

export function generateExampleText(): string {
  return `jan@example.com
piet@bedrijf.nl
marie@email.com`;
}

export function generateExampleExcel(): Blob {
  const worksheet = XLSX.utils.aoa_to_sheet([
    ['email', 'naam'],
    ['jan@example.com', 'Jan'],
    ['piet@bedrijf.nl', 'Piet'],
    ['marie@email.com', 'Marie']
  ]);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Contacten');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

export async function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error('Kon bestand niet lezen'));
    reader.readAsText(file);
  });
}

export function isAcceptedFileType(file: File): boolean {
  const acceptedTypes = [
    'text/csv',
    'text/plain',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];

  const acceptedExtensions = ['.csv', '.txt', '.xlsx', '.xls'];
  const hasValidExtension = acceptedExtensions.some(ext =>
    file.name.toLowerCase().endsWith(ext)
  );

  return acceptedTypes.includes(file.type) || hasValidExtension;
}

export async function parseExcelFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          reject(new Error('Geen data gevonden in bestand'));
          return;
        }

        const workbook = XLSX.read(data, { type: 'binary' });

        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          reject(new Error('Geen werkbladen gevonden in Excel bestand'));
          return;
        }

        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const csvText = XLSX.utils.sheet_to_csv(firstSheet);

        if (!csvText || csvText.trim().length === 0) {
          reject(new Error('Excel werkblad is leeg'));
          return;
        }

        resolve(csvText);
      } catch (error) {
        reject(new Error('Kon Excel bestand niet verwerken'));
      }
    };

    reader.onerror = () => reject(new Error('Kon Excel bestand niet lezen'));
    reader.readAsBinaryString(file);
  });
}
